import { Link } from 'react-router-dom'
import { BadgeCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { CommunityPost } from '@/lib/communityPostData'
import { useCommunityPostAuthorProfile } from '@/hooks/useCommunityPostAuthorProfile'

interface CommunityPostDetailAuthorRowProps {
  readonly post: CommunityPost
  readonly placeLabel: string
  readonly ageLabel: string
}

export function CommunityPostDetailAuthorRow({
  post,
  placeLabel,
  ageLabel,
}: CommunityPostDetailAuthorRowProps) {
  const { t } = useTranslation()
  const authorProfile = useCommunityPostAuthorProfile(
    post,
    String(t('community.anonymousHandle')),
  )

  const nameElement = authorProfile.profileHref ? (
    <Link
      to={authorProfile.profileHref}
      className="truncate text-sm font-semibold text-foreground hover:underline"
    >
      {authorProfile.authorDisplayName}
    </Link>
  ) : (
    <span className="truncate text-sm font-semibold text-foreground">
      {authorProfile.authorDisplayName}
    </span>
  )

  return (
    <div className="flex items-center gap-3">
      <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-2xl border-2 border-border bg-muted shadow-sm">
        {authorProfile.avatarUrl ? (
          <img
            src={authorProfile.avatarUrl}
            alt=""
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-sm font-semibold text-muted-foreground">
            {authorProfile.loading ? '' : authorProfile.avatarInitial}
          </span>
        )}
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-1">
          {nameElement}
          {authorProfile.showVerifiedArtistCheckmark && (
            <BadgeCheck
              className="h-4 w-4 shrink-0 text-blue-500"
              aria-label={String(t('board.verifiedArtistAria'))}
            />
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          {placeLabel}
          <span aria-hidden> · </span>
          {ageLabel}
        </p>
      </div>
    </div>
  )
}
